import { Box, useTheme, Stack } from '@mui/material';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import Form from 'components/Form';
import useI18n from 'hooks/useI18n';
import { useState, useEffect, useRef } from 'react';
import { Close } from '@mui/icons-material';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import * as S from './styles';
import useUser from 'store/user/useUser';

declare const PuzzleSDK: any;

interface Props {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  disabled?: boolean;
  isTestMode?: boolean;
}

function NarrativeRecord({
  register,
  setValue,
  watch,
  disabled,
  isTestMode,
}: Props) {
  const i18n = useI18n();
  const { palette } = useTheme();
  const [voiceOn, setVoiceOn] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const puzzleWsRef = useRef<any>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const { student_id } = useUser();

  // 음성 인식 결과를 처리하는 콜백 함수
  const sttCallback = (stt: string) => {
    if (!stt) return;
  };

  // TextArea onChange 핸들러
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue('narrativeNote', e.target.value, {
      shouldValidate: true,
    });
  };

  // textAreaRef의 값을 감시하는 useEffect
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        textAreaRef.current &&
        textAreaRef.current.value !== watch('narrativeNote')
      ) {
        setValue('narrativeNote', textAreaRef.current.value, {
          shouldValidate: true,
        });
      }
    }, 100); // 100ms마다 체크

    return () => clearInterval(interval);
  }, [setValue, watch]);

  // WebSocket 연결 상태를 처리하는 콜백 함수
  const connectedCallback = (status: boolean) => {
    console.log('connectedCallback:', status);
    setIsConnected(status);
  };

  const initializePuzzleSDK = () => {
    try {
      if (!PuzzleSDK) {
        console.error('PuzzleSDK is not loaded');
        return false;
      }

      const uri = process.env.REACT_APP_PUZZLE_AI_WS_URL;
      const appKey = process.env.REACT_APP_PUZZLE_AI_APP_KEY;
      const userId = student_id;
      const modelLanguage = 'ENG';

      const fullUri = `${uri}?appKey=${encodeURIComponent(
        appKey || ''
      )}&userId=${encodeURIComponent(
        userId || ''
      )}&modelLanguage=${encodeURIComponent(modelLanguage)}`;

      PuzzleSDK.createInstance(fullUri, connectedCallback, sttCallback);

      return true;
    } catch (error) {
      console.error('Failed to initialize PuzzleSDK:', error);
      return false;
    }
  };

  const handleVoiceConnect = () => {
    try {
      if (!puzzleWsRef.current) {
        const initialized = initializePuzzleSDK();
        if (!initialized) return;

        puzzleWsRef.current = PuzzleSDK.getInstance();
        puzzleWsRef.current.addBoard('narrative', '서술기록');
        puzzleWsRef.current.addItem('narrative', 'narrativeNote', '서술');
      }

      puzzleWsRef.current.restartRecord();
      setIsConnected(true);
    } catch (error) {
      console.error('Failed to connect:', error);
      setIsConnected(false);
    }
  };

  const handleVoiceDisconnect = () => {
    try {
      if (puzzleWsRef.current) {
        puzzleWsRef.current.stopRecord();
        setIsConnected(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const handleVoiceRestart = () => {
    try {
      if (puzzleWsRef.current) {
        puzzleWsRef.current.stopRecord();
        setTimeout(() => {
          puzzleWsRef.current.restartRecord();
        }, 200);
      }
    } catch (error) {
      console.error('Failed to restart:', error);
    }
  };

  const VoiceMenuOnHandler = () => {
    setVoiceOn(true);
  };

  const VoiceMenuOFFHandler = () => {
    handleVoiceDisconnect();
    setVoiceOn(false);
  };

  const openManual = () => {
    window.open(
      'https://smartnurse.notion.site/What-is-Voice-EMR-26272847337280e9ac36f6ef62ca69a8?pvs=74',
      '_blank'
    );
  };

  const handlePreventEvent = (e: React.ClipboardEvent | React.MouseEvent) => {
    if (isTestMode) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    return () => {
      if (puzzleWsRef.current) {
        handleVoiceDisconnect();
      }
    };
  }, []);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <S.VoiceMainButton
          type="button"
          onClick={VoiceMenuOnHandler}
          disabled={disabled}
          $isConnected={isConnected}
        >
          <SettingsVoiceIcon fontSize="small" />
        </S.VoiceMainButton>
        {!voiceOn ? null : (
          <S.VoiceControlsContainer $primaryColor={palette.primary.main}>
            <S.VoiceControlButton
              type="button"
              onClick={handleVoiceConnect}
              disabled={isConnected}
            >
              VOICE ON
            </S.VoiceControlButton>
            <S.VoiceControlButton
              type="button"
              onClick={handleVoiceRestart}
              disabled={!isConnected}
            >
              RESTART
            </S.VoiceControlButton>
            <S.VoiceControlButton
              type="button"
              onClick={handleVoiceDisconnect}
              disabled={!isConnected}
            >
              VOICE OFF
            </S.VoiceControlButton>
            <S.VoiceControlButton type="button" onClick={openManual}>
              MANUAL
            </S.VoiceControlButton>
            <S.PoweredByLink
              href="https://puzzle-ai.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <S.PoweredByText>
                <span>Powered by</span>
                <span style={{ fontWeight: 'bold' }}> PuzzleAI</span>
              </S.PoweredByText>
            </S.PoweredByLink>

            <S.CloseIconButton size="small" onClick={VoiceMenuOFFHandler}>
              <Close color="primary" sx={{ height: '18px' }} />
            </S.CloseIconButton>
          </S.VoiceControlsContainer>
        )}
      </Box>
      <Form.Item label={i18n('NARRATIVE.NARRATIVENOTE')}>
        <S.TextArea
          id="narrativeNote"
          ref={textAreaRef}
          defaultValue=""
          value={watch('narrativeNote')}
          onChange={handleTextAreaChange}
          aria-multiline="true"
          disabled={disabled}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
          rows={20}
        />
      </Form.Item>
    </Stack>
  );
}

export default NarrativeRecord;
